<template>
  <div class="open-hours-fields">
    <div v-for="dayIndex in dayOrder" :key="`oh-${dayIndex}`" class="day-row">
      <span class="day-label">{{ localize(dayKeys[dayIndex]) }}</span>

      <div class="day-controls">
        <template v-if="getEntry(dayIndex).start !== null">
          <NativeTime
            :model-value="getEntry(dayIndex).start || '09:00'"
            :is-rounded-to30-min="true"
            @update:model-value="(v: string) => updateTime(dayIndex, 'start', v)"
          />
          <span class="separator">&mdash;</span>
          <NativeTime
            :model-value="getEntry(dayIndex).end || '17:00'"
            :is-rounded-to30-min="true"
            @update:model-value="(v: string) => updateTime(dayIndex, 'end', v)"
          />
        </template>
        <span v-else class="closed-label">{{ localize('locations.form.openHours.closed') }}</span>
      </div>

      <GamSwitch
        class="day-switch"
        :type="GamSwitchValueType.SWITCH"
        :position="GamSwitchPosition.RIGHT"
        :name="`oh-day-${dayIndex}`"
        :checked="getEntry(dayIndex).start !== null"
        @update:checked="(v: boolean) => toggleDay(dayIndex, v)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import GamSwitch from '@/views/components/GamSwitch.vue';
import NativeTime from '@/views/components/NativeTime.vue';
import { GamSwitchPosition, GamSwitchValueType } from '@/views/composables/constants/components/gamSwitch.constants';

interface OpenHoursEntry {
  day: number;
  start: string | null;
  end: string | null;
}

const props = defineProps<{
  modelValue: OpenHoursEntry[];
}>();

const emit = defineEmits<{
  'update:modelValue': [value: OpenHoursEntry[]];
}>();

const dayOrder = [1, 2, 3, 4, 5, 6, 0];

const dayKeys: Record<number, string> = {
  0: 'locations.form.openHours.sunday',
  1: 'locations.form.openHours.monday',
  2: 'locations.form.openHours.tuesday',
  3: 'locations.form.openHours.wednesday',
  4: 'locations.form.openHours.thursday',
  5: 'locations.form.openHours.friday',
  6: 'locations.form.openHours.saturday',
};

const defaultEntry = (day: number): OpenHoursEntry => ({
  day,
  start: null,
  end: null,
});

function getEntry(day: number): OpenHoursEntry {
  return props.modelValue.find((e) => e.day === day) || defaultEntry(day);
}

function patchEntry(day: number, patch: Partial<OpenHoursEntry>): void {
  const existing = props.modelValue.filter((e) => e.day !== day);
  const current = getEntry(day);
  emit('update:modelValue', [...existing, { ...current, ...patch, day }]);
}

function updateTime(day: number, field: 'start' | 'end', value: string) {
  patchEntry(day, { [field]: value });
}

function toggleDay(day: number, open: boolean) {
  if (open) {
    patchEntry(day, { start: '09:00', end: '17:00' });
  } else {
    patchEntry(day, { start: null, end: null });
  }
}
</script>

<style lang="scss" scoped>
.open-hours-fields {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.day-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  min-height: 48px;
}

.day-label {
  width: 3rem;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-white-70);
  letter-spacing: 0.03em;
}

.day-controls {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex: 1;
  min-width: 0;
  flex-shrink: 0;
}

.day-switch {
  width: auto;
}

.separator {
  color: var(--color-white-30);
  flex-shrink: 0;
}

.closed-label {
  color: var(--color-white-30);
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.2em;
}
</style>
